import { useEffect, useState, useRef, type DragEvent } from 'react';
import { ActionIcon, Button, FileButton, Group, Input, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconFile, IconFileUpload, IconPaperclip, IconPhoto, IconX } from '@tabler/icons-react';
import type { FieldSchema } from '../types/schema';

interface AttachmentUploadFieldProps {
  field: FieldSchema;
  value: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  removeDisabled?: boolean;
  // Patient portal options. browseLabel replaces "Choose files"; hideLabel drops the field label
  // and built-in hint when the page renders its own; doneFiles marks files already uploaded.
  browseLabel?: string;
  hideLabel?: boolean;
  doneFiles?: ReadonlySet<File>;
}

const FILE_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EXTENSION_MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

// Some browsers report an empty MIME type; fall back to the file extension in that case.
function isAcceptedFile(file: File, acceptedFileTypes: string[]): boolean {
  if (acceptedFileTypes.includes(file.type)) return true;
  if (file.type !== '') return false;
  const ext = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
  const mapped = ext ? EXTENSION_MIME_TYPES[ext] : undefined;
  return mapped !== undefined && acceptedFileTypes.includes(mapped);
}

function formatSizeLimit(bytes: number): string {
  const mib = 1024 * 1024;
  return bytes % mib === 0 ? `${bytes / mib} MB` : formatFileSize(bytes);
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) {
    return IconPhoto;
  }
  return IconFile;
}

// "PDF, JPG, or PNG" from the configured MIME types.
function describeFileTypes(types: string[]): string {
  const labels = types.map((type) => FILE_TYPE_LABELS[type] ?? type);
  if (labels.length <= 2) return labels.join(' or ');
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

export default function AttachmentUploadField({ field, value, onChange, disabled, removeDisabled, browseLabel, hideLabel, doneFiles }: AttachmentUploadFieldProps) {
  const maxFiles = field.maxFiles ?? 3;
  const maxSizeBytes = field.maxSizeBytes ?? 5 * 1024 * 1024;
  const acceptedFileTypes = field.acceptedFileTypes ?? [];
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef<number>(0);

  // Stop the browser from navigating to a file dropped outside the drop box. React's handler on
  // the box runs first; this window listener only cancels the default navigation. Only file drags
  // are cancelled, so dragging text into inputs and textareas elsewhere on the page still works.
  useEffect(() => {
    const preventDefault = (event: globalThis.DragEvent) => {
      if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) event.preventDefault();
    };
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Picked and dropped files share one path, so every limit applies to both.
  const handleChange = (nextFiles: File[] | null) => {
    const files = nextFiles ?? [];
    if (files.length === 0) return;
    const combined = [...value, ...files];
    if (combined.length > maxFiles) {
      notifications.show({ color: 'red', message: `Select no more than ${maxFiles} attachments.` });
      return;
    }
    if (acceptedFileTypes.length > 0 && files.some((file) => !isAcceptedFile(file, acceptedFileTypes))) {
      notifications.show({ color: 'red', message: `Only ${describeFileTypes(acceptedFileTypes)} files can be attached.` });
      return;
    }
    const invalidFile = files.find((file) => file.size < 1 || file.size > maxSizeBytes);
    if (invalidFile) {
      notifications.show({
        color: 'red',
        message: `Each attachment must be larger than 0 bytes and no larger than ${formatSizeLimit(maxSizeBytes)}.`,
      });
      return;
    }
    onChange(combined);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !Array.from(event.dataTransfer.types).includes('Files')) {
      return;
    }
    dragDepth.current++;
    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragLeave = (_event: DragEvent<HTMLDivElement>) => {
    if (dragDepth.current <= 0) return;
    dragDepth.current--;
    if (dragDepth.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled) return;
    handleChange(Array.from(event.dataTransfer.files));
  };

  const accept = acceptedFileTypes.length > 0 ? acceptedFileTypes.join(',') : undefined;
  const hint = acceptedFileTypes.length > 0 ? `${describeFileTypes(acceptedFileTypes)} · up to ${formatSizeLimit(maxSizeBytes)} each` : undefined;

  return (
    <Input.Wrapper
      label={hideLabel ? undefined : (field.label ?? '')}
      description={field.helpText}
      required={field.required}
    >
      <Paper
        mt="xs"
        p="sm"
        withBorder
        pos="relative"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          backgroundColor: '#f8f9fa',
          borderStyle: isDragging ? 'dashed' : undefined,
          borderColor: isDragging ? 'var(--mantine-primary-color-filled)' : undefined,
        }}
      >
        {value.length === 0 ? (
          // Empty state - centered content with button inside
          <Stack gap="sm" align="center">
            <IconFileUpload size={24} style={{ color: 'var(--mantine-color-dimmed)' }} />
            <Text size="sm" c="dimmed" ta="center">
              {field.placeholder ?? 'Attach call notes, screenshots, or supporting documents'}
            </Text>
            <FileButton onChange={handleChange} multiple accept={accept} disabled={disabled}>
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  color="#000000"
                  disabled={disabled}
                  leftSection={<IconPaperclip size={15} />}
                >
                  {browseLabel ?? 'Choose files'}
                </Button>
              )}
            </FileButton>
          </Stack>
        ) : (
          // Files loaded - show list in gray box
          <Stack gap="xs" ta="left">
            {value.map((file) => {
              const FileIcon = getFileIcon(file.name);
              return (
                <Group key={`${file.name}-${file.size}-${file.lastModified}`} justify="space-between" gap="sm" wrap="nowrap">
                  <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    {doneFiles?.has(file)
                      ? <IconCircleCheck size={18} style={{ color: 'var(--mantine-color-green-7)', flexShrink: 0 }} role="img" aria-label="Uploaded" />
                      : <FileIcon size={18} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" truncate fw={500}>{file.name}</Text>
                      <Text size="xs" c="dimmed">{formatFileSize(file.size)}</Text>
                    </div>
                  </Group>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label={`Remove ${file.name}`}
                    title={`Remove ${file.name}`}
                    disabled={removeDisabled || doneFiles?.has(file)}
                    onClick={() => onChange(value.filter((selected) => selected !== file))}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        )}

        {isDragging && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(248, 249, 250, 0.92)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack gap="sm" align="center" py="xs">
              <IconFileUpload size={24} style={{ color: 'var(--mantine-primary-color-filled)' }} />
              <Text size="sm" ta="center">Drag and drop your documents here</Text>
            </Stack>
          </div>
        )}
      </Paper>

      <Group justify="space-between" mt={4} gap="xs">
        <Text size="xs" c="dimmed">{hideLabel ? undefined : hint}</Text>
        <Text size="xs" c="dimmed" aria-live="polite">{value.length} of {maxFiles} added</Text>
      </Group>

      {/* Button below gray box when files are loaded */}
      {value.length > 0 && value.length < maxFiles && (
        <FileButton onChange={handleChange} multiple accept={accept} disabled={disabled}>
          {(props) => (
            <Button
              {...props}
              size="xs"
              mt="xs"
              variant="light"
              color="#000000"
              disabled={disabled}
              leftSection={<IconPaperclip size={15} />}
            >
              Add more files
            </Button>
          )}
        </FileButton>
      )}
    </Input.Wrapper>
  );
}
